import { DatePipe } from "@angular/common";
import { Component, computed, Inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";

export interface AuditLogDiffDialogData {
  entityTable: string;
  entityId: string | null;
  action: string;
  changedByEmail: string | null;
  changedByType: "admin" | "vendor" | null;
  createdAt: string;
  oldValues: Record<string, any> | null;
  newValues: Record<string, any> | null;
}

interface DiffRow {
  key: string;
  oldValue: string;
  newValue: string;
  changed: boolean;
}

@Component({
  selector: "fh-audit-log-diff-dialog",
  standalone: true,
  imports: [DatePipe, MatDialogModule, MatButtonModule, MatIconModule],
  styles: [`
    mat-dialog-content { min-width: 640px; max-width: 900px; padding: 0 24px 8px; }

    .dialog-title {
      display: flex; align-items: center; gap: 8px;
      padding: 20px 24px 12px; border-bottom: 1px solid #f0f0f0;
    }
    .dialog-title mat-icon { color: #1976d2; }
    .dialog-title h2 { margin: 0; font-size: 18px; font-weight: 600; }

    .meta-row {
      display: flex; flex-wrap: wrap; gap: 16px 28px;
      padding: 16px 0; border-bottom: 2px solid #f0f0f0; font-size: 13px;
    }
    .meta-field .lbl { font-size: 11px; color: #999; }
    .meta-field .val { color: #333; }
    .role-tag {
      display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 99px;
      font-size: 10px; font-weight: 600; text-transform: uppercase;
      background: #eef2ff; color: #4338ca;
    }
    .action-badge {
      display: inline-block; padding: 2px 8px; border-radius: 99px;
      font-size: 11px; font-weight: 600; text-transform: uppercase;
    }
    .action-badge.insert { background: #dcfce7; color: #16a34a; }
    .action-badge.update { background: #fef9c3; color: #a16207; }
    .action-badge.delete { background: #fee2e2; color: #dc2626; }

    table { width: 100%; border-collapse: collapse; font-size: 13px; margin: 16px 0; }
    thead th {
      background: #f5f7fa; padding: 8px 10px; text-align: left;
      font-weight: 600; color: #444; border-bottom: 2px solid #e0e0e0;
    }
    tbody td { padding: 7px 10px; border-bottom: 1px solid #f0f0f0; vertical-align: top; word-break: break-word; }
    tbody tr.changed td.old-val { background: #fff1f0; color: #b91c1c; }
    tbody tr.changed td.new-val { background: #f0fdf4; color: #15803d; }
    .key-cell { font-weight: 500; color: #555; white-space: nowrap; }
    .empty-diff { text-align: center; padding: 32px 0; color: #aaa; font-size: 14px; }

    mat-dialog-actions { justify-content: flex-end; padding: 8px 24px 16px; }
  `],
  template: `
    <div class="dialog-title">
      <mat-icon>history</mat-icon>
      <h2>Change Details</h2>
    </div>

    <mat-dialog-content>
      <div class="meta-row">
        <div class="meta-field">
          <div class="lbl">Table</div>
          <div class="val">{{ _data.entityTable }}</div>
        </div>
        <div class="meta-field">
          <div class="lbl">Record ID</div>
          <div class="val">{{ _data.entityId || '—' }}</div>
        </div>
        <div class="meta-field">
          <div class="lbl">Action</div>
          <div class="val">
            <span class="action-badge" [class]="'action-badge ' + _data.action.toLowerCase()">
              {{ _data.action }}
            </span>
          </div>
        </div>
        <div class="meta-field">
          <div class="lbl">Changed By</div>
          <div class="val">
            {{ _data.changedByEmail || '—' }}
            @if (_data.changedByType) {
              <span class="role-tag">{{ _data.changedByType === 'admin' ? 'Admin' : 'Vendor' }}</span>
            }
          </div>
        </div>
        <div class="meta-field">
          <div class="lbl">When</div>
          <div class="val">{{ _data.createdAt | date: 'dd MMM yy, h:mm:ss a' }}</div>
        </div>
      </div>

      @if (rows().length === 0) {
        <div class="empty-diff">No field-level data recorded for this change.</div>
      } @else {
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Before</th>
              <th>After</th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows(); track row.key) {
              <tr [class.changed]="row.changed">
                <td class="key-cell">{{ row.key }}</td>
                <td class="old-val">{{ row.oldValue }}</td>
                <td class="new-val">{{ row.newValue }}</td>
              </tr>
            }
          </tbody>
        </table>
      }
    </mat-dialog-content>

    <mat-dialog-actions>
      <button mat-button (click)="close()">Close</button>
    </mat-dialog-actions>
  `,
})
export class AuditLogDiffDialogComponent {
  rows = computed<DiffRow[]>(() => this.buildRows());

  constructor(
    public readonly dialogRef: MatDialogRef<AuditLogDiffDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly _data: AuditLogDiffDialogData
  ) {}

  private buildRows(): DiffRow[] {
    const oldV = this._data.oldValues || {};
    const newV = this._data.newValues || {};
    const keys = Array.from(new Set([...Object.keys(oldV), ...Object.keys(newV)])).sort();

    return keys.map((key) => {
      const oldValue = this.format(oldV[key]);
      const newValue = this.format(newV[key]);
      return { key, oldValue, newValue, changed: oldValue !== newValue };
    });
  }

  private format(value: any): string {
    if (value === undefined || value === null) return "—";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }

  close(): void {
    this.dialogRef.close();
  }
}
